import { startOfDay } from 'date-fns';
import * as Yup from 'yup';

export const RaptorJobValidationSchema = () =>
  Yup.object({
    customerId: Yup.number().required('Customer is required').min(1, 'Please select a valid customer'),

    jobSiteId: Yup.number()
      .nullable()
      .when('customerId', {
        is: (customerId: number | null | undefined) => Boolean(customerId && customerId > 0),
        then: (schema) => schema.required('Job site is required').min(1, 'Please select a valid job site'),
        otherwise: (schema) => schema
      }),

    jobTypeId: Yup.number().required('Job type is required').min(1, 'Please select a valid job type'),

    jobDescription: Yup.string().max(1000, 'Job description must be less than 1000 characters'),

    recurrence: Yup.string().oneOf(['single', 'multi'], 'Please select a valid recurrence option').required('Recurrence is required'),

    startDate: Yup.date()
      .typeError('Please enter a valid date.')
      .required('Start date is required')
      .min(startOfDay(new Date()), 'Start date cannot be in the past'),

    estimatedDurationDays: Yup.number()
      .nullable()
      .when('recurrence', {
        is: 'multi',
        then: (schema) => schema.min(1, 'Duration must be at least 1 day'),
        otherwise: (schema) => schema
      }),

    endDate: Yup.date()
      .nullable()
      .typeError('Please enter a valid date.')
      .when(['recurrence', 'startDate'], {
        is: (recurrence: string, startDate: Date) => recurrence === 'multi' && Boolean(startDate),
        then: (schema) => schema.min(Yup.ref('startDate'), 'End date must be after start date'),
        otherwise: (schema) => schema
      })
      .min(startOfDay(new Date()), 'End date cannot be in the past'),

    startTime: Yup.date().nullable(),

    endTime: Yup.date()
      .nullable()

      .when('startTime', {
        is: (startTime: Date) => Boolean(startTime),
        then: (schema) => schema.min(Yup.ref('startTime'), 'End time must be after start time')
      }),

    showUpTime: Yup.date().nullable(),

    leaveByTime: Yup.date().nullable(),

    onJobTime: Yup.date().nullable(),

    notesForCrew: Yup.string().max(255, 'Notes for crew must be less than 255 characters'),

    jobNotes: Yup.string().max(255, 'Job notes must be less than 255 characters'),

    estimates: Yup.array()
      .of(
        Yup.object({
          metricType: Yup.string().required('Metric type is required'),
          quantity: Yup.number().required('Quantity is required').min(0.01, 'Quantity must be greater than 0'),
          unit: Yup.string().required('Unit is required')
        })
      )
      .default([]),

    groups: Yup.array()
      .of(
        Yup.object({
          id: Yup.string().required('Group ID is required'),
          employees: Yup.array()
            .of(
              Yup.object({
                employeeId: Yup.number().required('Employee is required').min(1, 'Please select a valid employee'),
                employeeName: Yup.string().required('Employee name is required'),
                roleId: Yup.number().required('Role is required').min(1, 'Please select a valid role'),
                roleName: Yup.string().required('Role name is required')
              })
            )
            .min(1, 'At least one employee is required per group')
            .required('Employees array is required'),
          equipment: Yup.object({
            equipmentId: Yup.number().required('Equipment is required').min(1, 'Please select valid equipment'),
            equipmentName: Yup.string().required('Equipment name is required'),
            equipmentClassId: Yup.number().nullable(),
            equipmentClass: Yup.string().nullable(),
            equipmentTypeId: Yup.number().nullable(),
            equipmentType: Yup.string().nullable(),
            equipmentSizeId: Yup.number().nullable(),
            equipmentSize: Yup.string().nullable(),
            equipmentStatus: Yup.string().nullable()
          }).required('Equipment details are required')
        })
      )
      .default([]),
    extraSystemIds: Yup.array().of(Yup.number()).default([])
  });

// Estimate interface
export interface Estimate {
  id: string;
  metricType: string;
  quantity: number | null;
  unit: string;
}

// Employee within a group interface
export interface GroupEmployee {
  employeeId: number | null;
  employeeName: string | null;
  roleId: number;
  roleName: string;
  status?: string[];
}

// Equipment details within a group
export interface GroupEquipment {
  equipmentId: number;
  equipmentName: string;
  equipmentClassId: number | null;
  equipmentClass: string | null;
  equipmentTypeId: number | null;
  equipmentType: string | null;
  equipmentSizeId: number | null;
  equipmentSize: string | null;
  equipmentStatus: string | null;
}

// Group interface
export interface Group {
  id: string;
  employees: GroupEmployee[];
  equipment: GroupEquipment;
}

// Base form values type for initial values (allows null for all nullable fields)
export interface RaptorJobFormValues {
  name?: string;
  customerId?: number | null;
  customerName?: string | null;
  jobSiteId?: number | null;
  jobTypeId?: number | null;
  jobDescription?: string; //description
  recurrence: 'single' | 'multi';
  startDate?: Date | null;
  estimatedDurationDays?: number | null;
  endDate?: Date | null;
  startTime?: Date | null;
  endTime?: Date | null;
  showUpTime?: Date | null;
  leaveByTime?: Date | null;
  onJobTime?: Date | null;
  callerId?: number | null;
  calledAt?: string | null;
  confirmedBy?: number | null;
  confirmedAt?: string | null;
  cod?: boolean;
  poNumber?: string;
  salesAgentId?: number | null;
  salesRegionId?: number | null;
  vendorId?: number | null;
  flag1Id?: number | null;
  flag2Id?: number | null;
  flag3Id?: number | null;
  notesForCrew?: string; //crewNotes
  jobNotes?: string; // internalNotes
  estimates: Estimate[];
  groups: Group[];
  items?: number[];
  extraSystemIds?: number[];
  [key: string]: unknown;
}

export type ValidatedRaptorJobFormValues = Yup.InferType<ReturnType<typeof RaptorJobValidationSchema>>;
